import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {League} from "../../model/league";
import {LeaguesService} from "../../services/leagues.service";
import {ScheduleSummary, SchedulesService} from "../../services/schedules.service";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatTableModule} from "@angular/material/table";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatTooltipModule} from "@angular/material/tooltip";
import {FormsModule} from "@angular/forms";
import {RouterLink} from "@angular/router";
import {DatePipe, NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {CreateLeagueDialogComponent} from "../create-league-dialog/create-league-dialog.component";

// 'pending'    – summary still loading
// 'upcoming'   – at least one game scheduled in the future (keep polling it)
// 'ended'      – schedule exists but is fully in the past → archive candidate
// 'noData'     – no scheduled-games docs at all
// 'notTracked' – server never scrapes a schedule for this league (not licenced)
// 'skipped'    – archived, so the server ignores it anyway
type ScheduleState = 'pending' | 'upcoming' | 'ended' | 'noData' | 'notTracked' | 'skipped' | 'error';

interface ScheduleInfo extends Partial<ScheduleSummary> {
  state: ScheduleState;
}

interface LeagueRow {
  league: League;
  schedule: ScheduleInfo;
}

type TriState = 'all' | 'yes' | 'no';

@Component({
  selector: 'app-admin-leagues',
  templateUrl: './admin-leagues.component.html',
  styleUrls: ['./admin-leagues.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTooltipModule,
    FormsModule,
    RouterLink,
    DatePipe,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
  ]
})
export class AdminLeaguesComponent implements OnInit, OnDestroy {

  readonly columns = ['id', 'name', 'federation', 'poweredBy', 'licenced', 'archived', 'schedule', 'actions'];

  rows: LeagueRow[] = [];
  federations: string[] = [];
  totalCount = 0;
  loading = true;

  // Filters. Archived leagues are hidden by default — they are the ones already
  // dealt with, and the point of this screen is finding the ones still polling.
  search = '';
  federation = '';
  licenced: TriState = 'all';
  archived: TriState = 'no';
  onlyArchiveCandidates = false;

  private leagues: League[] = [];
  private schedules = new Map<string, ScheduleInfo>();
  private subscriptions: Subscription[] = [];

  constructor(
    private leaguesService: LeaguesService,
    private dialog: MatDialog,
    private schedulesService: SchedulesService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(this.leaguesService.getAllLeagues().subscribe(leagues => {
      this.leagues = [...leagues].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.totalCount = this.leagues.length;
      this.federations = Array.from(new Set(this.leagues.map(l => l.federation).filter(f => !!f))).sort();
      this.loading = false;
      this.loadSchedules();
      this.applyFilters();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  applyFilters(): void {
    const term = this.normalize(this.search);
    this.rows = this.leagues
      .filter(league => this.matches(league, term))
      .map(league => ({league, schedule: this.scheduleOf(league)}))
      .filter(row => !this.onlyArchiveCandidates || this.isArchiveCandidate(row.schedule));
  }

  resetFilters(): void {
    this.search = '';
    this.federation = '';
    this.licenced = 'all';
    this.archived = 'no';
    this.onlyArchiveCandidates = false;
    this.applyFilters();
  }

  // Re-runs the per-league schedule reads (they are a one-off snapshot, not a
  // live stream, so archiving elsewhere or a fresh scrape needs a manual pull).
  refreshSchedules(): void {
    this.schedules.clear();
    this.loadSchedules();
    this.applyFilters();
  }

  isArchiveCandidate(schedule: ScheduleInfo): boolean {
    return schedule.state === 'ended' || schedule.state === 'noData';
  }

  createLeague(): void {
    this.dialog.open(CreateLeagueDialogComponent, {autoFocus: true});
  }

  archiveLeague(leagueId: string, archived: boolean): Promise<void> {
    return this.leaguesService.archiveLeague(leagueId, archived);
  }

  deleteLeague(leagueId: string): Promise<void> {
    return this.leaguesService.deleteLeague(leagueId);
  }

  // Flags the league for an immediate re-scrape by ball-score-server. The list is
  // a live stream, so the flag flipping back to false (within ~1 min) is what
  // ends the button's pending state — nothing to poll here.
  forceScheduleRefresh(leagueId: string): Promise<void> {
    return this.leaguesService.forceScheduleRefresh(leagueId);
  }

  // Public for the template: only a league the server actually scrapes can honour
  // the flag. Mirrors ball-score-server's `isRealLeague` — and note what is NOT
  // in it any more: the licence. Since the server's auto-discovery work it also
  // tracks unlicenced leagues, on a once-a-day cadence, so they do have a
  // schedule and they do clear the force flag. Keeping the licence test here
  // made every auto-discovered league render as "not tracked" and stopped
  // loadSchedules() from even reading its schedule, which looked exactly like
  // the server had not fetched one.
  isServerTracked(league: League): boolean {
    return !!league.url && !['new', 'unknown'].includes(league.id);
  }

  private matches(league: League, term: string): boolean {
    if (this.federation && league.federation !== this.federation) return false;
    if (!this.matchesTriState(this.licenced, league.licenced)) return false;
    if (!this.matchesTriState(this.archived, league.archived)) return false;
    if (!term) return true;
    return this.normalize(league.name).includes(term) || this.normalize(league.id).includes(term);
  }

  private matchesTriState(filter: TriState, value: boolean | undefined): boolean {
    if (filter === 'all') return true;
    return filter === 'yes' ? !!value : !value;
  }

  private normalize(value: string | undefined): string {
    return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  private scheduleOf(league: League): ScheduleInfo {
    return this.schedules.get(league.id) || {state: this.staticState(league)};
  }

  // Mirrors ball-score-server's own filter (isRealLeague): anything it skips has
  // no schedule to read and costs no proxy traffic.
  private staticState(league: League): ScheduleState {
    if (league.archived) return 'skipped';
    if (!this.isServerTracked(league)) return 'notTracked';
    return 'pending';
  }

  private loadSchedules(): void {
    this.leagues
      .filter(league => this.staticState(league) === 'pending' && !this.schedules.has(league.id))
      .forEach(league => {
        this.schedules.set(league.id, {state: 'pending'});
        this.subscriptions.push(this.schedulesService.getScheduleSummary(league.id).subscribe({
          next: summary => {
            this.schedules.set(league.id, {
              ...summary,
              state: !summary.hasSchedule ? 'noData' : summary.next ? 'upcoming' : 'ended',
            });
            this.applyFilters();
          },
          error: () => {
            this.schedules.set(league.id, {state: 'error'});
            this.applyFilters();
          },
        }));
      });
  }
}
